import useAppSelector from '../../../hooks/useAppSelector'
import {
  getUpdatesOursSelector,
  getAddOrUpdateEntries
} from '../../data/dataSlice'
import { createLogger } from '../../../app/logger'
import { useMemo } from 'react'
import { S3CheckinEntry } from '../../../app/types'

// eslint-disable-next-line no-unused-vars
const logger = createLogger({
  prefix: 'useEntries'
})

export function useEntries(entriesFromApi: S3CheckinEntry[]): S3CheckinEntry[] {
  const updatesOurs = useAppSelector(getUpdatesOursSelector)
  // used when SSE message is received, with payload that contains entire entry
  const addOrUpdateEntries = useAppSelector(getAddOrUpdateEntries)

  return useMemo(() => {
    // hash maps for quick lookups
    const addOrUpdateMap = new Map<number, S3CheckinEntry>(
      (addOrUpdateEntries || []).map((entry) => [entry.entry_id, entry])
    )
    const updatesOursMap = new Map<number, typeof updatesOurs[number]>(
      updatesOurs.map((update) => [update.entryId, update])
    )
    const mapped = new Set<number>()

    const updated: S3CheckinEntry[] = entriesFromApi.map((entry) => {
      // safeguard if we already have this entry in reducer (e.g. sent twice from an api)
      if (mapped.has(entry.entry_id)) {
        return null
      }
      mapped.add(entry.entry_id)

      // find and delete to prevent duplicates from being returned
      const addOrUpdateEntry = addOrUpdateMap.get(entry.entry_id) || null
      if (addOrUpdateEntry) {
        addOrUpdateMap.delete(entry.entry_id)
      }

      const updatedEntry = updatesOursMap.get(entry.entry_id)
      if (updatedEntry) {
        const bib =
          addOrUpdateEntry && updatedEntry.synced
            ? addOrUpdateEntry.entry_bib
            : !addOrUpdateEntry && !updatedEntry.synced
              // eslint-disable-next-line indent
              ? updatedEntry.bib
              // eslint-disable-next-line indent
              : entry.entry_bib

        const tag =
          addOrUpdateEntry && updatedEntry.synced
            ? addOrUpdateEntry.entry_tag
            : !addOrUpdateEntry && !updatedEntry.synced
              // eslint-disable-next-line indent
              ? updatedEntry.tag
              // eslint-disable-next-line indent
              : entry.entry_tag

        const checkIn =
          addOrUpdateEntry && updatedEntry.synced
            ? addOrUpdateEntry.entry_check_in
            : !addOrUpdateEntry && !updatedEntry.synced
              // eslint-disable-next-line indent
              ? updatedEntry.checkedIn
              // eslint-disable-next-line indent
              : entry.entry_check_in

        return {
          ...entry,
          entry_bib: bib,
          entry_tag: tag,
          entry_check_in: checkIn as 0 | 1 | null,
        }
      }

      return addOrUpdateEntry ?? entry
    }).filter(x => x !== null) as S3CheckinEntry[]

    return updated.concat(
      Array.from(addOrUpdateMap.values())
    )
  }, [
    entriesFromApi,
    addOrUpdateEntries,
    updatesOurs,
  ])
}
